import { debounceMaxWait, postCartUpdates, updateCartCountBadge } from '@trade/utils';
import { dispatchCartUpdated } from '@trade/events';

/**
 * @typedef {object} TradeCartUpdateQueueOptions
 * @property {HTMLElement} component
 * @property {import('@trade/cart-api').TradeCartStore} store
 * @property {string} source
 * @property {() => string} getSectionsUrl
 * @property {() => string[]} getSectionIds
 * @property {number} [debounceMs]
 * @property {number} [maxWaitMs]
 * @property {(message: string, variantIds: string[]) => void} [onError]
 * @property {() => void} [onSuccess]
 */

const DEFAULT_DEBOUNCE_MS = 600;
const DEFAULT_MAX_WAIT_MS = 2000;

export class TradeCartUpdateQueue {
  /** @type {HTMLElement} */
  component;

  /** @type {import('@trade/cart-api').TradeCartStore} */
  #store;

  /** @type {string} */
  #source;

  /** @type {() => string} */
  #getSectionsUrl;

  /** @type {() => string[]} */
  #getSectionIds;

  /** @type {(message: string, variantIds: string[]) => void | undefined} */
  #onError;

  /** @type {(() => void) | undefined} */
  #onSuccess;

  /** @type {Map<string, number>} */
  #pendingUpdates = new Map();

  /** @type {boolean} */
  #flushing = false;

  /** @type {boolean} */
  #needsFollowUpFlush = false;

  /** @type {ReturnType<typeof debounceMaxWait>} */
  #scheduleFlush;

  /**
   * @param {TradeCartUpdateQueueOptions} options
   */
  constructor(options) {
    this.component = options.component;
    this.#store = options.store;
    this.#source = options.source;
    this.#getSectionsUrl = options.getSectionsUrl;
    this.#getSectionIds = options.getSectionIds;
    this.#onError = options.onError;
    this.#onSuccess = options.onSuccess;

    const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
    const maxWaitMs = options.maxWaitMs ?? DEFAULT_MAX_WAIT_MS;

    this.#scheduleFlush = debounceMaxWait(() => {
      this.#flush();
    }, debounceMs, maxWaitMs);
  }

  /**
   * @param {string | number} variantId
   * @param {number} quantity
   * @param {{ productId?: string }} [options]
   */
  enqueueUpdate(variantId, quantity, options = {}) {
    const key = String(variantId);
    const committed = this.#store.getCommittedQty(key);

    if (quantity === committed && !this.#pendingUpdates.has(key)) {
      return;
    }

    this.#store.setPending(key, quantity, options);
    this.#pendingUpdates.set(key, quantity);
    this.#scheduleFlush();
  }

  /**
   * @param {Record<string, number>} updates
   */
  enqueueBatch(updates) {
    for (const [variantId, quantity] of Object.entries(updates)) {
      const key = String(variantId);
      this.#store.setPending(key, quantity);
      this.#pendingUpdates.set(key, quantity);
    }

    this.#scheduleFlush();
  }

  /**
   * Cancel debounce timers and flush pending updates immediately.
   * @returns {Promise<void>}
   */
  flushNow() {
    this.#scheduleFlush.cancel();
    return this.#flush();
  }

  async #flush() {
    if (this.#flushing) {
      this.#needsFollowUpFlush = true;
      return;
    }

    if (this.#pendingUpdates.size === 0) return;

    this.#flushing = true;
    const sent = Object.fromEntries(this.#pendingUpdates);
    const variantIds = Object.keys(sent);

    for (const id of variantIds) {
      this.#pendingUpdates.delete(id);
    }

    try {
      const data = await postCartUpdates({
        updates: sent,
        sectionIds: this.#getSectionIds(),
        sectionsUrl: this.#getSectionsUrl(),
      });

      if (data.errors) {
        this.#store.revert(variantIds);

        const message = typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
        this.#onError?.(message, variantIds);
        return;
      }

      const needsReflush = this.#store.commitFromFlush(sent, data.items, {
        itemCount: data.item_count,
        totalPrice: data.total_price,
      });

      for (const variantId of needsReflush) {
        const line = this.#store.get(variantId);
        if (line?.pending != null) {
          this.#pendingUpdates.set(String(variantId), line.pending);
        }
      }

      updateCartCountBadge(this.#store.getDisplayItemCount());
      this.#onSuccess?.();

      const committedIds = variantIds.filter((id) => !needsReflush.includes(id));

      if (committedIds.length > 0) {
        dispatchCartUpdated({
          changedVariantIds: committedIds,
          itemCount: data.item_count,
          totalPrice: data.total_price,
          source: this.#source,
          origin: this.component,
        });
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.#store.revert(variantIds);
      this.#onError?.(error?.message || 'Failed to update cart', variantIds);
    } finally {
      this.#flushing = false;

      if (this.#needsFollowUpFlush || this.#pendingUpdates.size > 0) {
        this.#needsFollowUpFlush = false;
        await this.#flush();
      }
    }
  }
}

/**
 * @param {TradeCartUpdateQueueOptions} options
 */
export function createCartUpdateQueue(options) {
  return new TradeCartUpdateQueue(options);
}
